import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { UploadIcon } from "@heroicons/react/outline";
import { Project, SupportingDocumentMetadata } from "types";
import {
  prepareSupportingUpload,
  SupportingUpload,
  useSupportingDocuments,
} from "hooks/useSupportingDocuments";
import { SupportingDocuments } from "./SupportingDocuments";
import { SupportingDocumentDialog } from "./SupportingDocumentDialog";
import { defaultSupportingMetadata } from "./supportingMetadata";

export const SupportingDocumentsManager = ({
  project,
}: {
  project: Project;
}) => {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const { upload } = useSupportingDocuments(project.projectdata.crn);
  const [queue, setQueue] = useState<SupportingUpload[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(false);
  const [notice, setNotice] = useState("");
  const [dragging, setDragging] = useState(false);
  const busyRef = useRef(false);
  const enqueue = (files: FileList | null) => {
    if (!files?.length || queue.length || busyRef.current) return;
    const next: SupportingUpload[] = [];
    const invalid: string[] = [];
    Array.from(files).forEach((file) => {
      try {
        next.push(prepareSupportingUpload(file));
      } catch {
        invalid.push(file.name);
      }
    });
    setQueue(next);
    setTotal(next.length);
    setError(false);
    setNotice(
      invalid.length
        ? `${t("supportingDocuments.invalidFile")}: ${invalid.join(", ")}`
        : "",
    );
    if (input.current) input.current.value = "";
  };
  const save = async (metadata: SupportingDocumentMetadata) => {
    if (busyRef.current || !queue[0]) return;
    busyRef.current = true;
    setError(false);
    const current = queue[0];
    current.metadata = metadata;
    try {
      await upload.mutateAsync(current);
      setQueue((previous) => previous.slice(1));
      setNotice(t("documentLibrary.uploaded", { name: metadata.title }));
    } catch {
      setError(true);
    } finally {
      busyRef.current = false;
    }
  };
  return (
    <div className="mt-6">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            {t("documentLibrary.tab")}
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-500">
            {t("documentLibrary.intro")}
          </p>
        </div>
        <button
          type="button"
          onClick={() => input.current?.click()}
          className="inline-flex items-center gap-2 rounded-md bg-cyan-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-cyan-700"
        >
          <UploadIcon className="h-4 w-4" />
          {t("documentLibrary.upload")}
        </button>
      </div>
      <input
        ref={input}
        type="file"
        multiple
        className="sr-only"
        aria-label={t("supportingDocuments.chooseFiles")}
        onChange={(e) => enqueue(e.target.files)}
      />
      <div
        role="region"
        aria-label={t("supportingDocuments.dropTitle")}
        className={`flex flex-wrap items-center justify-between gap-4 rounded-xl border-2 border-dashed p-6 ${dragging ? "border-cyan-500 bg-cyan-50" : "border-gray-200 bg-white"}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          enqueue(e.dataTransfer.files);
        }}
      >
        <div className="flex items-center gap-4">
          <div className="rounded-lg bg-cyan-50 p-3 text-cyan-700">
            <UploadIcon className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">
              {t("supportingDocuments.dropTitle")}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              {t("documentLibrary.uploadHint")}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => input.current?.click()}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700"
        >
          {t("supportingDocuments.chooseFiles")}
        </button>
      </div>
      {notice && (
        <p role="status" className="mt-3 break-words text-sm text-gray-600">
          {notice}
        </p>
      )}
      <SupportingDocuments project={project} manage />
      {queue[0] && (
        <SupportingDocumentDialog
          projectId={project.projectdata.crn}
          file={queue[0].file}
          initial={
            queue[0].metadata || defaultSupportingMetadata(queue[0].file.name)
          }
          onClose={() => {
            if (!busyRef.current) setQueue([]);
          }}
          onSave={save}
          busy={upload.isLoading}
          error={error}
          position={
            total > 1 ? `${total - queue.length + 1} / ${total}` : undefined
          }
        />
      )}
    </div>
  );
};
